import React, { useRef, useState, useEffect } from 'react';
import { Stack } from 'modules/common/components/Stack/Stack';
import { Icon } from 'icons';
import Styles from './AudioPlayer.module.scss';

interface AudioPlayerProps {
  audioUrl: string;
}

export const AudioPlayer: React.FC<AudioPlayerProps> = ({ audioUrl }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const audioPlayer = useRef<HTMLAudioElement>(null);
  const progressBar = useRef<HTMLInputElement>(null);
  const animationRef = useRef<number>();

  useEffect(() => {
    const seconds = Math.floor(audioPlayer.current?.duration || 0);
    setDuration(seconds);
    if (progressBar.current) {
      progressBar.current.max = seconds.toString();
    }
  }, [audioPlayer?.current?.onloadedmetadata, audioPlayer?.current?.readyState]);

  const calculateTime = (secs: number) => {
    const minutes = Math.floor(secs / 60);
    const returnedMinutes = minutes < 10 ? `0${minutes}` : `${minutes}`;
    const seconds = Math.floor(secs % 60);
    const returnedSeconds = seconds < 10 ? `0${seconds}` : `${seconds}`;
    return `${returnedMinutes}:${returnedSeconds}`;
  };

  const togglePlayPause = () => {
    if (!isPlaying) {
      audioPlayer.current?.play();
      animationRef.current = requestAnimationFrame(whilePlaying);
    } else {
      audioPlayer.current?.pause();
      cancelAnimationFrame(animationRef.current!);
    }
    setIsPlaying(!isPlaying);
  };

  const whilePlaying = () => {
    if (progressBar.current && audioPlayer.current) {
      progressBar.current.value = audioPlayer.current.currentTime.toString();
      changePlayerCurrentTime();
      animationRef.current = requestAnimationFrame(whilePlaying);
    }
  };

  const changeRange = () => {
    if (audioPlayer.current && progressBar.current) {
      audioPlayer.current.currentTime = Number(progressBar.current.value);
      changePlayerCurrentTime();
    }
  };

  const changePlayerCurrentTime = () => {
    if (progressBar.current) {
      setCurrentTime(Number(progressBar.current.value));
      progressBar.current.style.setProperty(
        '--seek-before-width',
        `${(Number(progressBar.current.value) / duration) * 100}%`
      );
    }
  };

  return (
    <div className={Styles.audioPlayer}>
      <audio ref={audioPlayer} src={audioUrl} preload="metadata" />
      <Stack direction="column" spacing={2}>
        <Stack.Item>
          <Stack alignItems="center" spacing={2}>
            <button onClick={togglePlayPause} className={Styles.playPause}>
              {isPlaying ? (
                <Icon name="pause" size={24} />
              ) : (
                <Icon name="play_arrow" size={24} />
              )}
            </button>
            <div className={Styles.currentTime}>{calculateTime(currentTime)}</div>
            <input
              type="range"
              className={Styles.progressBar}
              defaultValue="0"
              ref={progressBar}
              onChange={changeRange}
            />
            <div className={Styles.duration}>
              {duration && !isNaN(duration) && calculateTime(duration)}
            </div>
          </Stack>
        </Stack.Item>
      </Stack>
    </div>
  );
}; 